import { Training } from 'model/NewMultipleBooking';
import { Moment } from 'moment-timezone';
import { pickedSessionSlotsOnDay, SessionDateSlot } from 'utils/sessionDateSlots';

export const mergeTrainingDayWithClock = (day: Moment, clock: Moment, tz: string): Moment => {
    const d = day.clone().tz(tz).startOf('day');
    const c = clock.clone().tz(tz);
    return d.clone().hour(c.hour()).minute(c.minute()).second(0).millisecond(0);
};

const getTrainingDurationInMinutes = (training: Training, fallbackDate: Moment, tz: string): number => {
    if (training.from && training.to) {
        const diff = training.to.clone().tz(tz).diff(training.from.clone().tz(tz), 'minutes');
        if (diff > 0) {
            return diff;
        }
    }

    const duration = training.service?.duration;
    if (duration && duration > 0) {
        return duration;
    }

    return 60;
};

const uniqSelectedDays = (selected: Array<Moment>, tz: string): Array<Moment> => {
    const uniqByDay = new Map<string, Moment>();
    selected.forEach(d => {
        const dayStart = d.clone().tz(tz).startOf('day');
        uniqByDay.set(dayStart.format('YYYY-MM-DD'), dayStart);
    });
    return Array.from(uniqByDay.values()).sort((a, b) => a.valueOf() - b.valueOf());
};

const mapSelectedDaysWithClock = (
    selected: Array<Moment>,
    fromClock: Moment,
    toClock: Moment | undefined,
    fallbackDate: Moment,
    training: Training,
    tz: string
): Array<SessionDateSlot> => {
    const durationInMinutes = getTrainingDurationInMinutes(training, fallbackDate, tz);
    const sortedDays = uniqSelectedDays(selected, tz);

    return sortedDays.flatMap((dayStart): Array<SessionDateSlot> => {
        // A date with picked sessions books one appointment per session instead of the typed time.
        const picked = pickedSessionSlotsOnDay(
            training.sessionSlots,
            dayStart.format('YYYY-MM-DD'),
            durationInMinutes,
            tz
        );
        if (picked.length > 0) {
            return picked;
        }

        const date = mergeTrainingDayWithClock(dayStart, fromClock, tz);
        const dateEnd = toClock
            ? mergeTrainingDayWithClock(dayStart, toClock, tz)
            : date.clone().add(durationInMinutes, 'minutes');
        return [{ date, dateEnd }];
    });
};

export const normalizeTrainingForSlotSave = (training: Training, tz: string): Training => ({
    ...training,
    from: training.from?.clone().tz(tz),
    to: training.to?.clone().tz(tz),
    selectedDates: training.selectedDates?.map(d => d.clone().tz(tz).startOf('day'))
});

export const getTrainingDateSlotsForSave = (
    training: Training,
    fallbackDate: Moment,
    tz: string,
    isEditMode: boolean
): Array<SessionDateSlot> => {
    const normalizedTraining = normalizeTrainingForSlotSave(training, tz);
    const fallbackInTz = fallbackDate.clone().tz(tz);

    if (isEditMode) {
        const from = (normalizedTraining.from || fallbackInTz).clone().tz(tz);
        const to =
            normalizedTraining.to?.clone().tz(tz) ||
            from.clone().add(getTrainingDurationInMinutes(normalizedTraining, fallbackInTz, tz), 'minutes');
        return [{ date: from, dateEnd: to }];
    }

    const selected = normalizedTraining.selectedDates ?? [];
    const fromClock = (normalizedTraining.from || fallbackInTz).clone().tz(tz);
    const toClock = normalizedTraining.to?.clone().tz(tz);

    if (selected.length === 0) {
        const to =
            toClock ||
            fromClock.clone().add(getTrainingDurationInMinutes(normalizedTraining, fallbackInTz, tz), 'minutes');
        return [{ date: fromClock.clone(), dateEnd: to.clone() }];
    }

    return mapSelectedDaysWithClock(selected, fromClock, toClock, fallbackInTz, normalizedTraining, tz);
};

export const hasMultipleTrainingDates = (training: Training, fallbackDate: Moment, tz: string): boolean =>
    getTrainingDateSlotsForSave(training, fallbackDate, tz, false).length > 1;
